import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Send, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card } from '@/components/ui/card';

const MentionDropdown = ({ users, filter, onSelect, position }) => {
  const filtered = users.filter(u =>
    u.name.toLowerCase().includes(filter.toLowerCase())
  ).slice(0, 6);

  if (filtered.length === 0) return null;

  return (
    <div
      className="absolute bottom-full mb-2 left-0 w-64 bg-zinc-900 border border-zinc-700 rounded-sm shadow-lg overflow-hidden z-50"
      data-testid="mention-dropdown"
    >
      {filtered.map((u) => (
        <button
          key={u.user_id}
          data-testid={`mention-user-${u.user_id}`}
          onClick={() => onSelect(u)}
          className="w-full flex items-center gap-3 px-3 py-2 hover:bg-zinc-800 transition-colors text-left"
        >
          <Avatar className="w-7 h-7">
            <AvatarImage src={u.picture} />
            <AvatarFallback className="bg-primary text-white text-xs">
              {u.name?.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <span className="text-sm font-medium truncate">{u.name}</span>
        </button>
      ))}
    </div>
  );
};

const MessageText = ({ text, chatUsers }) => {
  if (!chatUsers || chatUsers.length === 0) return <span>{text}</span>;

  // Build sorted list of mention spans
  const spans = [];
  for (const user of chatUsers) {
    const escaped = user.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = new RegExp(`@${escaped}(?=\\s|$|[^\\w])`, 'gi');
    let m;
    while ((m = pattern.exec(text)) !== null) {
      spans.push({ start: m.index, end: m.index + m[0].length });
    }
  }
  spans.sort((a, b) => a.start - b.start);

  // Remove overlaps
  const clean = [];
  let lastEnd = 0;
  for (const s of spans) {
    if (s.start >= lastEnd) { clean.push(s); lastEnd = s.end; }
  }

  if (clean.length === 0) return <span>{text}</span>;

  const parts = [];
  let cursor = 0;
  clean.forEach((span, i) => {
    if (cursor < span.start) parts.push(<span key={`t${i}`}>{text.slice(cursor, span.start)}</span>);
    parts.push(<span key={`m${i}`} className="font-black underline decoration-2">{text.slice(span.start, span.end)}</span>);
    cursor = span.end;
  });
  if (cursor < text.length) parts.push(<span key="tail">{text.slice(cursor)}</span>);

  return <>{parts}</>;
};

const Chat = () => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [ws, setWs] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [user, setUser] = useState(null);
  const [sending, setSending] = useState(false);
  const [chatUsers, setChatUsers] = useState([]);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pollIntervalRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchMessages = useCallback(async () => {
    try {
      const response = await axios.get(`/api/chat/messages`, { withCredentials: true });
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch messages');
    }
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await axios.get(`/api/auth/me`, { withCredentials: true });
        setUser(response.data);
      } catch (error) {
        toast.error('Failed to fetch user data');
      }
    };

    const fetchChatUsers = async () => {
      try {
        const response = await axios.get(`/api/chat/users`, { withCredentials: true });
        setChatUsers(response.data);
      } catch (error) {
        console.error('Failed to fetch chat users');
      }
    };

    fetchUser();
    fetchMessages();
    fetchChatUsers();
  }, [fetchMessages]);

  useEffect(() => {
    if (!user) return;

    const connectWebSocket = () => {
      const getCookie = (name) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
      };

      const token = getCookie('session_token') || localStorage.getItem('auth_token');

      if (!token) {
        startPolling();
        return;
      }

      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${wsProtocol}//${window.location.host}`;
      const socket = new WebSocket(`${wsUrl}/ws/chat?token=${token}`);

      socket.onopen = () => {
        setWsConnected(true);
        stopPolling();
      };

      socket.onmessage = (event) => {
        const message = JSON.parse(event.data);
        setMessages((prev) => [...prev, message]);
      };

      socket.onerror = () => {
        setWsConnected(false);
      };

      socket.onclose = () => {
        setWsConnected(false);
        startPolling();
        reconnectTimeoutRef.current = setTimeout(() => {
          connectWebSocket();
        }, 5000);
      };

      setWs(socket);
    };

    connectWebSocket();

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      stopPolling();
    };
  }, [user]);

  const startPolling = () => {
    if (!pollIntervalRef.current) {
      pollIntervalRef.current = setInterval(fetchMessages, 3000);
    }
  };

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setNewMessage(value);

    // Check if user is typing a mention
    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = value.slice(0, cursorPos);
    const atMatch = textBeforeCursor.match(/@(\w*)$/);

    if (atMatch) {
      setShowMentions(true);
      setMentionFilter(atMatch[1]);
    } else {
      setShowMentions(false);
      setMentionFilter('');
    }
  };

  const handleMentionSelect = (selectedUser) => {
    const cursorPos = inputRef.current?.selectionStart || newMessage.length;
    const textBeforeCursor = newMessage.slice(0, cursorPos);
    const atIndex = textBeforeCursor.lastIndexOf('@');
    const textAfterMention = newMessage.slice(cursorPos);

    const newText = newMessage.slice(0, atIndex) + `@${selectedUser.name} ` + textAfterMention;
    setNewMessage(newText);
    setShowMentions(false);
    setMentionFilter('');
    inputRef.current?.focus();
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    const messageText = newMessage.trim();
    setShowMentions(false);

    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ message: messageText }));
      setNewMessage('');
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(`/api/chat/send`, { message: messageText }, { withCredentials: true });
      setMessages((prev) => [...prev, response.data]);
      setNewMessage('');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowMentions(false);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Header */}
      <div className="glass-header p-4 md:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <Users className="w-6 h-6 text-primary" />
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight uppercase">Community Chat</h1>
              <p className="text-sm text-muted-foreground">Connect with the brotherhood</p>
            </div>
          </div>
        </div>
      </div>

      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto p-4 md:p-6 scrollbar-hide">
        <div className="max-w-4xl mx-auto space-y-4">
          {messages.length === 0 ? (
            <div className="text-center text-muted-foreground py-12">
              <p>No messages yet. Start the conversation!</p>
            </div>
          ) : (
            messages.map((msg) => {
              const isOwnMessage = msg.user_id === user?.user_id;
              return (
                <div
                  key={msg.message_id}
                  data-testid={`chat-message-${msg.message_id}`}
                  className={`flex gap-3 ${isOwnMessage ? 'flex-row-reverse' : 'flex-row'}`}
                >
                  <Avatar className="w-10 h-10 flex-shrink-0">
                    <AvatarImage src={msg.user_picture} />
                    <AvatarFallback className="bg-primary text-white">
                      {msg.user_name?.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className={`flex flex-col ${isOwnMessage ? 'items-end' : 'items-start'} max-w-[70%]`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium">{msg.user_name}</span>
                      <span className="text-xs text-muted-foreground">{formatTime(msg.timestamp)}</span>
                    </div>
                    <Card
                      className={`p-3 ${
                        isOwnMessage
                          ? 'bg-primary text-white border-primary'
                          : 'bg-zinc-900 border-zinc-800'
                      }`}
                    >
                      <p className="text-sm break-words">
                        <MessageText text={msg.message} chatUsers={chatUsers} />
                      </p>
                    </Card>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Message Input */}
      <div className="border-t border-border p-4 bg-card">
        <form onSubmit={handleSendMessage} className="max-w-4xl mx-auto flex gap-2 relative">
          {showMentions && (
            <MentionDropdown
              users={chatUsers}
              filter={mentionFilter}
              onSelect={handleMentionSelect}
            />
          )}
          <Input
            ref={inputRef}
            data-testid="chat-message-input"
            type="text"
            value={newMessage}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Type your message... Use @ to mention"
            className="flex-1 bg-zinc-900/50 border-zinc-800 focus:border-primary focus:ring-1 focus:ring-primary rounded-sm h-12"
          />
          <Button
            type="submit"
            data-testid="send-message-button"
            disabled={!newMessage.trim() || sending}
            className="bg-primary text-white hover:bg-primary/90 rounded-sm font-bold uppercase tracking-wider px-6"
          >
            <Send className="w-5 h-5" />
          </Button>
        </form>
      </div>
    </div>
  );
};

export default Chat;
